import { MemberAvatar } from '@/components/projects/MemberAvatar'
import { cn } from '@/lib/utils'
import { groupByRelativeDate } from '@/lib/relativeDateGroups'
import { formatDateShort } from '@/lib/timeFormatters'
import type { EntityHistoryEntry } from '@/types'
import { actorName, describeEntry, type EntityHistoryFieldLabels } from './entityHistoryText'

interface EntityHistoryListProps {
  entries: EntityHistoryEntry[]
  entityLabel: string
  fieldLabels: EntityHistoryFieldLabels
  selectedChangeGroupId: string | null
  detailRegionId: string
  onSelect: (changeGroupId: string) => void
}

export function EntityHistoryList({
  entries,
  entityLabel,
  fieldLabels,
  selectedChangeGroupId,
  detailRegionId,
  onSelect,
}: EntityHistoryListProps) {
  const groups = groupByRelativeDate(entries, entry => entry.performedAt)

  return (
    <div className="space-y-4">
      {groups.map(group => (
        <div key={`${group.label}-${group.items[0].changeGroupId}`} className="space-y-1">
          <p
            className="px-2 text-xs font-semibold text-muted-foreground"
            data-testid="entity-history-date-group"
          >
            {group.label}
          </p>

          <ul className="space-y-1">
            {group.items.map(entry => {
              const isSelected = entry.changeGroupId === selectedChangeGroupId

              return (
                <li key={entry.changeGroupId}>
                  <button
                    type="button"
                    onClick={() => onSelect(entry.changeGroupId)}
                    aria-current={isSelected}
                    aria-expanded={isSelected}
                    aria-controls={detailRegionId}
                    data-testid="entity-history-entry"
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-muted/50',
                      isSelected && 'bg-muted',
                    )}
                  >
                    <MemberAvatar name={actorName(entry)} className="h-7 w-7 shrink-0" />

                    <span className="min-w-0 flex-1 text-sm">
                      <span className="font-semibold">{actorName(entry)}</span>{' '}
                      <span className="text-muted-foreground">
                        {describeEntry(entry, entityLabel, fieldLabels)}
                      </span>
                    </span>

                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatDateShort(entry.performedAt)}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </div>
  )
}
